import React from 'react';
import { Code, Cloud, Shield, BarChart3, Database, Smartphone, Cog, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import './Services.css';

const services = [
  {
    icon: <Code size={32} />,
    title: 'Custom Software Development',
    desc: 'We design and build bespoke enterprise software — from CRM platforms and ERP systems to SaaS products and internal tools. Every solution is architected for scalability, security, and maintainability.',
    features: ['Web Applications', 'Enterprise Software', 'API Development', 'Microservices Architecture'],
    img: 'https://images.unsplash.com/photo-1461749280684-dccba630e2f6?w=600&q=80',
  },
  {
    icon: <Cloud size={32} />,
    title: 'Cloud Solutions & DevOps',
    desc: 'Modernize your infrastructure with cloud-native architectures. We handle migration, optimization, and continuous delivery pipelines across AWS, Azure, and Google Cloud Platform.',
    features: ['Cloud Migration', 'DevOps & CI/CD', 'Infrastructure as Code', 'Cost Optimization'],
    img: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8?w=600&q=80',
  },
  {
    icon: <Shield size={32} />,
    title: 'Cybersecurity Services',
    desc: 'Protect your digital assets with comprehensive security solutions. We provide penetration testing, security audits, compliance frameworks, and ongoing threat monitoring.',
    features: ['Penetration Testing', 'Security Audits', 'GDPR/ISO Compliance', 'SOC as a Service'],
    img: 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=600&q=80',
  },
  {
    icon: <BarChart3 size={32} />,
    title: 'IT Consulting & Strategy',
    desc: 'Navigate complex technology decisions with expert guidance. We provide CTO-level advisory services, digital transformation roadmaps, and technology stack evaluations.',
    features: ['Digital Transformation', 'Technology Roadmaps', 'Vendor Selection', 'IT Governance'],
    img: 'https://images.unsplash.com/photo-1552664730-d307ca884978?w=600&q=80',
  },
  {
    icon: <Database size={32} />,
    title: 'Data Analytics & BI',
    desc: 'Transform raw data into strategic intelligence. We build data warehouses, analytics pipelines, and interactive dashboards that power smarter business decisions.',
    features: ['Data Engineering', 'Business Intelligence', 'ML/AI Integration', 'Real-time Analytics'],
    img: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=600&q=80',
  },
  {
    icon: <Smartphone size={32} />,
    title: 'Mobile App Development',
    desc: 'Create seamless mobile experiences for iOS and Android. From consumer apps to enterprise mobility solutions, we deliver intuitive, performant applications.',
    features: ['iOS & Android', 'React Native', 'Flutter', 'PWA Development'],
    img: 'https://images.unsplash.com/photo-1512941937669-90a1b58e7e9c?w=600&q=80',
  },
];

const process = [
  { num: '01', title: 'Discovery', desc: 'We deep-dive into your business goals, technical requirements, and constraints to define the perfect solution.' },
  { num: '02', title: 'Architecture', desc: 'Our experts design a robust technical architecture that is scalable, secure, and future-proof.' },
  { num: '03', title: 'Development', desc: 'Agile sprints with weekly demos, transparent progress tracking, and continuous integration.' },
  { num: '04', title: 'Deployment', desc: 'Rigorous QA, staged releases, and seamless deployment with zero-downtime strategies.' },
  { num: '05', title: 'Support', desc: 'Ongoing maintenance, monitoring, and evolution as your business grows and needs change.' },
];

export default function Services() {
  return (
    <div className="services-page">
      <section className="page-hero">
        <div className="container">
          <span className="section-label">What We Offer</span>
          <h1>World-Class <span>Technology Services</span></h1>
          <p>Comprehensive software engineering and consulting services, delivered by specialists who understand your industry.</p>
        </div>
      </section>

      {/* Services List */}
      <section className="section">
        <div className="container">
          <div className="services-list">
            {services.map((s, i) => (
              <div className={`service-row ${i % 2 !== 0 ? 'reverse' : ''}`} key={i}>
                <div className="service-row-img">
                  <img src={s.img} alt={s.title} />
                  <div className="service-row-num">{String(i + 1).padStart(2, '0')}</div>
                </div>
                <div className="service-row-content">
                  <div className="service-row-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <div className="gold-line" />
                  <p>{s.desc}</p>
                  <ul className="service-features">
                    {s.features.map((f, j) => (
                      <li key={j}><span className="feat-dot" />{f}</li>
                    ))}
                  </ul>
                  <Link to="/contact" className="btn-outline" style={{ marginTop: '1.5rem' }}>
                    Enquire Now <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="section process-section">
        <div className="container">
          <div className="section-header center">
            <span className="section-label">How We Work</span>
            <h2 className="section-title">Our <span>Proven Process</span></h2>
            <p className="section-desc" style={{ margin: '0 auto' }}>A structured, transparent methodology that delivers consistent results — on time and on budget.</p>
          </div>
          <div className="process-steps">
            {process.map((p, i) => (
              <div className="process-step" key={i}>
                <div className="process-num">{p.num}</div>
                <h4>{p.title}</h4>
                <p>{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech Stack */}
      <section className="section tech-section">
        <div className="container">
          <div className="section-header center">
            <span className="section-label">Technology Stack</span>
            <h2 className="section-title">Tools of <span>Our Craft</span></h2>
          </div>
          <div className="tech-grid">
            {['React', 'Node.js', 'Python', 'Java', 'AWS', 'Docker', 'Kubernetes', 'PostgreSQL', 'MongoDB', 'Redis', 'TypeScript', 'Flutter'].map((tech, i) => (
              <div className="tech-chip" key={i}>
                <Cog size={13} />
                {tech}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
